/**
 * Hook for fetching ISO-week task buckets that populate the board's week
 * selector. Mirrors the task-list filter (minus the deadline-week selection,
 * which the caller strips via `buildBucketFilterParams`) so the chip counts
 * reflect what's present absent any week filter.
 */

import { useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import { buildGraphQLFilter, type TaskFilterParams } from "@/hooks/useTasks";

export type WeekBucket = {
  year: number;
  week: number;
  count: number;
  hasOverdue: boolean;
};

export const GET_TASK_WEEK_BUCKETS = gql`
  query GetTaskWeekBuckets($filter: TaskFilterInput) {
    taskWeekBuckets(filter: $filter) {
      weeks {
        year
        week
        count
        hasOverdue
      }
      noDeadlineCount
      totalCount
    }
  }
`;

interface TaskWeekBucketsResult {
  taskWeekBuckets: {
    weeks: WeekBucket[];
    noDeadlineCount: number;
    totalCount: number;
  };
}

export function useTaskWeekBuckets(filterParams: TaskFilterParams): {
  buckets: WeekBucket[];
  totalCount: number;
  noDeadlineCount: number;
  loading: boolean;
  refetch: () => void;
} {
  // Match the kanban, which only shows top-level tasks.
  const filter = useMemo(
    () => buildGraphQLFilter({ parentIdIsNull: true, ...filterParams }),
    [filterParams],
  );

  const { data, loading, refetch } = useQuery<TaskWeekBucketsResult>(
    GET_TASK_WEEK_BUCKETS,
    {
      variables: { filter },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
    },
  );

  return {
    buckets: data?.taskWeekBuckets.weeks ?? [],
    totalCount: data?.taskWeekBuckets.totalCount ?? 0,
    noDeadlineCount: data?.taskWeekBuckets.noDeadlineCount ?? 0,
    loading,
    refetch: () => {
      void refetch();
    },
  };
}
